import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import UserMessageVisualization from './UserMessageVisualization';
import { motion, AnimatePresence } from 'framer-motion';

const PreferencesMonitoring = ({ apiBaseUrl, apiKey }) => {
  const [currentCompany, setCurrentCompany] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [latestPreference, setLatestPreference] = useState('');
  const [showVisualization, setShowVisualization] = useState(false);

  useEffect(() => {
    const fetchCurrentCompany = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/demo-scenario`, {
          headers: {
            'X-Access-Token': apiKey
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setCurrentCompany(data.demo_scenario);
      } catch (error) {
        console.error("Failed to fetch current company:", error);
      }
    };
    fetchCurrentCompany();
  }, [apiBaseUrl, apiKey]);

  useEffect(() => {
    if (currentCompany) {
      const usersRef = collection(db, 'companies', currentCompany, 'preferences');
      const unsubscribe = onSnapshot(usersRef, (snapshot) => {
        const usersList = snapshot.docs.map(doc => doc.id);
        setUsers(usersList);
      });
      return () => unsubscribe();
    }
  }, [currentCompany]);

  useEffect(() => {
    if (currentCompany && selectedUser) {
      const preferencesRef = doc(db, 'companies', currentCompany, 'preferences', selectedUser);
      const unsubscribe = onSnapshot(preferencesRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const preferences = data.preferences || [];
          if (preferences.length > 0) {
            setLatestPreference(preferences[preferences.length - 1]);
          }
        } else {
          setLatestPreference('');
        }
      });
      return () => unsubscribe();
    }
  }, [currentCompany, selectedUser]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setShowVisualization(false);
    setTimeout(() => setShowVisualization(true), 100);
  };

  return (
    <div className="grid grid-cols-3 gap-4 font-sans">
      <div className="col-span-1 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-2">Users</h2>
        <div className="h-[calc(100vh-250px)] overflow-y-auto">
          {users.map((user) => (
            <div
              key={user}
              className={`p-3 mb-2 cursor-pointer rounded-md transition-all duration-200 ${
                selectedUser === user
                  ? 'bg-teal-100 text-teal-800'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              onClick={() => handleUserSelect(user)}
            >
              {user}
            </div>
          ))}
        </div>
      </div>

      <div className="col-span-2">
        <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-2">Preferences</h2>
        <AnimatePresence>
          {showVisualization && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.5 }}
            >
              <UserMessageVisualization latestPhrase={latestPreference} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PreferencesMonitoring;
